import React from 'react';
import { ArrowUpRightIcon, CheckIcon, CircleDotIcon, MoveDownIcon } from 'lucide-react';
import { Link } from 'react-router-dom';
import { motion, useReducedMotion, type Transition } from 'framer-motion';
import { ResearchSource } from '../components/ResearchSource';
const businessModel = [['CAMPAIGN PROGRAMMES', 'Structured around a defined product experience, delivery scope, and campaign review.'], ['PLATFORM PARTNERSHIPS', 'A model for connecting brands, activation partners, and participating locations.'], ['ENTERPRISE INFRASTRUCTURE', 'A tailored operating direction for organisations building recurring product-experience capability.']];
const focus = ['Prove the product experience format with focused campaigns.', 'Develop repeatable operating patterns with brand and activation partners.', 'Turn campaign learning into a clearer planning layer for the next experience.'];
const revealTransition: Transition = {
  duration: 0.5,
  ease: [0.22, 1, 0.36, 1]
};
export function InvestorPage() {
  const reduceMotion = useReducedMotion();
  const reveal = reduceMotion ? false : {
    opacity: 0,
    y: 18
  };
  return <div className="min-h-screen overflow-x-hidden bg-[#f7f5ef] text-[#11110f]" data-id="element-946" data-mp="39eetd8on5apq">
      <header className="border-b border-black/15 bg-[#f7f5ef]" data-id="element-947" data-mp="35agumz38a1x7">
        <div className="mx-auto flex max-w-[1440px] items-center justify-between px-5 py-4 md:px-10" data-id="element-948" data-mp="30cfbyf9ps4m9">
          <Link to="/" className="flex items-center gap-2.5" aria-label="Sampler home" data-id="element-949" data-mp="2vd2el0l7vf5f">
            <span className="grid h-8 w-8 place-items-center rounded-sm bg-[#11110f] text-white" data-id="element-950" data-mp="2qmm1jhlb5vjl">
              <span className="h-2.5 w-2.5 rounded-full bg-current" data-id="element-951" data-mp="2lniu5qhuvd8g" />
            </span>
            <span className="font-display text-xl font-bold" data-id="element-952" data-mp="2ll6fbm7e9izm">SAMPLER</span>
          </Link>
          <nav className="flex items-center gap-4 text-xs font-extrabold" aria-label="Investor navigation" data-id="element-953" data-mp="2hjumz8x4yjnq">
            <Link className="hover:text-[#ff4d17]" to="/brand" data-id="element-954" data-mp="2cop30fnanf2a">
              BRAND PORTAL
            </Link>
            <a className="rounded-full bg-[#11110f] px-4 py-2 text-white hover:bg-black/80" href="#contact" data-id="element-955" data-mp="1y05hsx9y55qr">
              CONTACT
            </a>
          </nav>
        </div>
      </header>

      <main data-id="element-956" data-mp="22wvynmtks5d8">
        <section className="relative min-h-[82vh] overflow-hidden border-b border-black/15 px-5 py-20 md:px-10 md:py-28" aria-labelledby="investor-heading" data-id="element-957" data-mp="1o1zkyu2zypsr">
          <motion.div initial={reduceMotion ? false : {
          opacity: 0,
          x: 24,
          rotate: 6
        }} animate={{
          opacity: 1,
          x: 0,
          rotate: 0
        }} transition={{
          ...revealTransition,
          delay: reduceMotion ? 0 : 0.2
        }} className="pointer-events-none absolute right-[8%] top-[20%] hidden h-32 w-20 border-4 border-black bg-[#ff4d17] lg:block" aria-hidden="true" data-id="element-958" data-mp="1t1kq5m8rfudx" />
          <motion.div initial={reduceMotion ? false : {
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          ...revealTransition,
          delay: reduceMotion ? 0 : 0.3
        }} className="pointer-events-none absolute bottom-[15%] right-[18%] hidden h-20 w-20 rounded-full border-4 border-black bg-[#72c7e8] lg:block" aria-hidden="true" data-id="element-959" data-mp="2zprdbsvq8yvc" />
          <div className="relative mx-auto max-w-[1280px]" data-id="element-960" data-mp="12rbpgl5ae9sf">
            <motion.p initial={reveal} animate={{
            opacity: 1,
            y: 0
          }} transition={revealTransition} className="text-xs font-extrabold tracking-[0.16em] text-[#ff4d17]" data-id="element-961" data-mp="12sv8ukm8p0yf">
              SAMPLER / INVESTOR OVERVIEW
            </motion.p>
            <motion.h1 id="investor-heading" initial={reveal} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            ...revealTransition,
            delay: reduceMotion ? 0 : 0.08
          }} className="mt-7 max-w-5xl font-display text-[clamp(4rem,10vw,10rem)] font-bold leading-[0.78] tracking-[-0.075em]" data-id="element-962" data-mp="54uw1qzocgby">
              Product experience is a media opportunity.
            </motion.h1>
            <motion.div initial={reveal} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            ...revealTransition,
            delay: reduceMotion ? 0 : 0.16
          }} className="mt-14 grid gap-8 border-t border-black/15 pt-7 md:grid-cols-[1fr_0.7fr]" data-id="element-963" data-mp="1qiz68a8u44gz">
              <p className="max-w-2xl text-xl leading-relaxed text-black/80" data-id="element-964" data-mp="2utu8zsugcz59">
                Sampler is building a connected experience layer for physical
                products: discovery, participation, collection, and feedback in
                one campaign flow.
              </p>
              <div className="border-l-4 border-[#ff4d17] pl-5 text-sm font-bold leading-relaxed" data-id="element-965" data-mp="38p62jwwvgjkl">
                INVESTOR MATERIAL / DIRECTIONAL OVERVIEW
                <br data-id="element-966" data-mp="3ig0ot1a4bj61" />
                <span className="text-black/65" data-id="element-967" data-mp="3igk8amff2fm6">
                  Research references are traceable below. Market size,
                  traction, pricing, and financial data are intentionally not
                  included without approved sources.
                </span>
              </div>
            </motion.div>
          </div>
        </section>

        <section className="bg-black px-5 py-24 text-white md:px-10 md:py-32" aria-labelledby="problem-heading" data-id="element-968" data-mp="1bt30jdfb8p00">
          <div className="mx-auto max-w-[1280px]" data-id="element-969" data-mp="16sx65phnooe8">
            <PitchHeading number="01" eyebrow="CATEGORY PROBLEM" title="Media can follow attention. Product sampling often cannot." headingId="problem-heading" data-id="element-970" data-mp="16vt4vg1bvh3q" />
            <div className="mt-16 grid gap-4 md:grid-cols-3" data-id="element-971" data-mp="1zhck08p1x0nz">
              {['Products are distributed without a lasting consumer relationship.', 'Experience is treated as a one-off logistical event.', 'Learning is disconnected from the next campaign decision.'].map((item, index) => <RevealCard key={item} index={index} className="border border-white/20 p-6" data-id="element-972" data-mp="1ktrk0nnh3hka" data-mpi={index}>
                  <p className="font-display text-4xl text-[#ff4d17]" data-id="element-973" data-mp="2i8lv2megzxho">
                    0{index + 1}
                  </p>
                  <p className="mt-12 text-lg font-bold leading-tight" data-id="element-974" data-mp="2dh450tdzjhbh">
                    {item}
                  </p>
                </RevealCard>)}
            </div>
          </div>
        </section>

        <section className="bg-white px-5 py-24 md:px-10 md:py-32" aria-labelledby="opportunity-heading" data-id="element-975" data-mp="giod9akfc83a">
          <div className="mx-auto grid max-w-[1280px] gap-12 lg:grid-cols-[0.8fr_1.2fr]" data-id="element-976" data-mp="1ts5lema4x37">
            <PitchHeading number="02" eyebrow="MARKET OPPORTUNITY" title="A large physical category. A still-forming digital layer." headingId="opportunity-heading" data-id="element-977" data-mp="6sv22ao71uzk" />
            <motion.div initial={reveal} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true,
            amount: 0.3
          }} transition={revealTransition} className="space-y-8" data-id="element-978" data-mp="3t14njvuzlek6">
              <p className="text-xl leading-relaxed text-black/80" data-id="element-979" data-mp="3k1hmxca30cs6">
                Brands already invest in products, media, retail, events, and
                activation. Sampler’s opportunity is to make the physical
                product itself a more connected, addressable campaign moment.
              </p>
              <ResearchSource sourceId="RI-01" status="pending" title="Market size and category-spend evidence" summary="No approved quantitative market-sizing, category-spend, market-share, or comparable-company source is attached to this deck. Therefore no market number is presented." methodology="Add only approved, dated sources with the relevant geography, category definition, methodology, and calculation logic. Treat any future market model as a separate, auditable appendix." data-id="element-980" data-mp="32rm3e4cmofrt" />
            </motion.div>
          </div>
        </section>

        <section className="bg-[#062c50] px-5 py-24 text-white md:px-10 md:py-32" aria-labelledby="positioning-heading" data-id="element-981" data-mp="3vfhyx5qgo1n0">
          <div className="mx-auto max-w-[1280px]" data-id="element-982" data-mp="3qgf3md0a99s8">
            <PitchHeading number="03" eyebrow="POSITIONING" title={<>
                  Not another sampling channel.{' '}
                  <span className="text-[#72c7e8]" data-id="element-984" data-mp="1w2xewmo566vm">
                    A product experience layer.
                  </span>
                </>} headingId="positioning-heading" light data-id="element-983" data-mp="3qeltuoudi9g0" />
            <div className="mt-16 grid gap-4 md:grid-cols-3" data-id="element-985" data-mp="1a7nlxgwr0rpk">
              <PositionCard title="MEDIA" copy="Turns attention into a planned moment of participation." data-id="element-986" data-mp="1f709y4zcw4tq" />
              <PositionCard title="PRODUCT" copy="Connects discovery to a physical catch and collection flow." data-id="element-987" data-mp="1jztccm7cr12n" />
              <PositionCard title="LEARNING" copy="Creates a direction for following experience, redemption, and feedback." data-id="element-988" data-mp="1p0h2z6ro5fwc" />
            </div>
          </div>
        </section>

        <section className="bg-[#f7f5ef] px-5 py-24 md:px-10 md:py-32" aria-labelledby="how-heading" data-id="element-989" data-mp="wa8uy7qukbvh">
          <div className="mx-auto max-w-[1280px]" data-id="element-990" data-mp="1178qhoq2lw8c">
            <PitchHeading number="04" eyebrow="HOW IT WORKS" title="One product. One connected campaign flow." headingId="how-heading" data-id="element-991" data-mp="15a5fpya48ql9" />
            <div className="mt-14 grid gap-px border border-black/15 bg-black/15 md:grid-cols-4" data-id="element-992" data-mp="2cn750840pagy">
              {['CREATE', 'DISCOVER', 'REDEEM', 'LEARN'].map((item, index) => <RevealCard key={item} index={index} className="min-h-[230px] bg-[#f7f5ef] p-6" data-id="element-993" data-mp="51yuokrwltcf" data-mpi={index}>
                  <p className="text-xs font-extrabold text-[#ff4d17]" data-id="element-994" data-mp="10vety9uvxb7">
                    0{index + 1}
                  </p>
                  <p className="mt-16 font-display text-4xl font-bold" data-id="element-995" data-mp="3se9er4o6nf5h">
                    {item}
                  </p>
                  <p className="mt-4 text-sm font-bold leading-relaxed text-black/70" data-id="element-996" data-mp="3nfokdlvm3poy">
                    {['Define the product, place, audience, and experience direction.', 'Give people a reason to join a product moment.', 'Connect the catch to participating collection points.', 'Review the experience as a campaign direction.'][index]}
                  </p>
                </RevealCard>)}
            </div>
            <p className="mt-6 text-xs font-bold text-black/60" data-id="element-997" data-mp="34j6memq66tk9">
              PLATFORM DIRECTION — product and operational capabilities are
              subject to validation and rollout.
            </p>
          </div>
        </section>

        <section className="bg-[#ff4d17] px-5 py-24 text-black md:px-10 md:py-32" aria-labelledby="model-heading" data-id="element-998" data-mp="1ss4hgzpugtwc">
          <div className="mx-auto max-w-[1280px]" data-id="element-999" data-mp="1nu2ysfwbywle">
            <PitchHeading number="05" eyebrow="BUSINESS MODEL" title="Revenue can follow the campaign — and the programme behind it." headingId="model-heading" data-id="element-1000" data-mp="1nve10ba4nwae" />
            <div className="mt-14 grid gap-4 lg:grid-cols-3" data-id="element-1001" data-mp="1wv120vyy302x">
              {businessModel.map(([title, copy], index) => <RevealCard key={title} index={index} className="border border-black/30 bg-[#f7f5ef] p-6" data-id="element-1002" data-mp="1956pygmg9fjq" data-mpi={index}>
                  <p className="text-xs font-extrabold tracking-[0.12em] text-[#ff4d17]" data-id="element-1003" data-mp="154d6bzg1imwn">
                    ILLUSTRATIVE BUSINESS MODEL
                  </p>
                  <h3 className="mt-8 font-display text-3xl font-bold" data-id="element-1004" data-mp="1e2ymxp1ybrus">
                    {title}
                  </h3>
                  <p className="mt-5 leading-relaxed text-black/80" data-id="element-1005" data-mp="qcl0qf61sn84">{copy}</p>
                </RevealCard>)}
            </div>
            <p className="mt-7 max-w-3xl text-sm font-bold leading-relaxed text-black/70" data-id="element-1006" data-mp="lej935uk3fl1">
              Commercial assumptions, pricing, margin, revenue mix, and unit
              economics are not included here and require validation before use
              in investment materials.
            </p>
          </div>
        </section>

        <section className="bg-white px-5 py-24 md:px-10 md:py-32" aria-labelledby="advantage-heading" data-id="element-1007" data-mp="2t0b8ctuqmld3">
          <div className="mx-auto grid max-w-[1280px] gap-10 lg:grid-cols-2" data-id="element-1008" data-mp="3gqf289i4j6qw">
            <PitchHeading number="06" eyebrow="DISTRIBUTION ADVANTAGE" title="The product is already where the experience needs to go." headingId="advantage-heading" data-id="element-1009" data-mp="3goaufgh3tnlc" />
            <motion.div initial={reveal} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true,
            amount: 0.4
          }} transition={revealTransition} className="border-l-4 border-[#ff4d17] pl-6 text-xl font-bold leading-relaxed" data-id="element-1010" data-mp="36rg3pjr2orhm">
              Sampler is designed to connect the participants already involved
              in a product moment: brand teams, media and activation partners,
              participating locations, and consumers.
            </motion.div>
          </div>
          <div className="mx-auto mt-16 grid max-w-[1280px] gap-4 md:grid-cols-3" data-id="element-1011" data-mp="17h7k3nk8b9uy">
            {['BRAND TEAMS', 'ACTIVATION & MEDIA PARTNERS', 'PARTICIPATING LOCATIONS'].map((item, index) => <RevealCard key={item} index={index} className="border border-black/15 p-6" data-id="element-1012" data-mp="12gjrixgcqkym" data-mpi={index}>
                <CircleDotIcon className="text-[#ff4d17]" aria-hidden="true" data-id="element-1013" data-mp="xh6yfuyw0hnl" />
                <p className="mt-12 text-sm font-extrabold tracking-[0.12em]" data-id="element-1014" data-mp="sinaxq4naxt4">
                  {item}
                </p>
              </RevealCard>)}
          </div>
        </section>

        <section className="bg-black px-5 py-24 text-white md:px-10 md:py-32" aria-labelledby="focus-heading" data-id="element-1015" data-mp="jktp594ibmk7">
          <div className="mx-auto max-w-[1280px]" data-id="element-1016" data-mp="2iw4ab0s5g53w">
            <PitchHeading number="07" eyebrow="GO-TO-MARKET & CURRENT FOCUS" title="Earn the right to scale through repeatable experiences." headingId="focus-heading" light data-id="element-1017" data-mp="2dxt2evlf04ob" />
            <div className="mt-14 grid gap-5 md:grid-cols-3" data-id="element-1018" data-mp="2lf7xhtd5n9i9">
              {focus.map((item, index) => <RevealCard key={item} index={index} className="border-t border-white/25 pt-6" data-id="element-1019" data-mp="2qe2eqqvh4q09" data-mpi={index}>
                  <p className="font-display text-4xl text-[#ff4d17]" data-id="element-1020" data-mp="2v8yiupdzj3tq">
                    0{index + 1}
                  </p>
                  <p className="mt-10 text-lg font-bold leading-relaxed" data-id="element-1021" data-mp="22ig6syva9s46">
                    {item}
                  </p>
                </RevealCard>)}
            </div>
            <p className="mt-12 text-sm font-bold text-white/70" data-id="element-1022" data-mp="27jdnt84k4g43">
              Current focus is directional. Traction, forecasts, capital
              requirements, and milestones are pending validation.
            </p>
          </div>
        </section>

        <section className="bg-white px-5 py-24 md:px-10 md:py-32" aria-labelledby="research-index-heading" data-id="element-1023" data-mp="3nwaui4ilxr89">
          <div className="mx-auto max-w-[1280px]" data-id="element-1024" data-mp="3rw39ayylgtm2">
            <PitchHeading number="08" eyebrow="RESEARCH INDEX" title="A pitch should show its working." headingId="research-index-heading" data-id="element-1025" data-mp="3qfz5ppjrplln" />
            <p className="mt-7 max-w-2xl text-lg leading-relaxed text-black/75" data-id="element-1026" data-mp="3lhxo791zpuca">
              This ledger separates the published category context available
              today from the business evidence that still needs approved
              research. It is designed to make the deck easier to audit, update,
              and discuss.
            </p>
            <div className="mt-12 space-y-4" data-id="element-1027" data-mp="d72vn2knt0oe">
              <ResearchSource sourceId="RI-02 / SR-01" status="verified" title="Sveriges Annonsörer + Novus — advertising fatigue in Sweden" summary="This 2022 public source supports the homepage category-context figures: 52% said advertising disrupts the experience, 49% said there is too much advertising, and 44% said advertising is annoying." methodology="Published 25 Aug 2022. The article describes an annual study of Swedes’ attitudes to advertising. It is contextual consumer research, not Sampler campaign performance, market size, or proof of ROI." published="25 AUG 2022" sourceUrl="https://www.sverigesannonsorer.se/aktuellt/efter-trendbrottet-under-pandemin/" sourceLabel="OPEN PUBLISHED SOURCE" data-id="element-1028" data-mp="89b449nxfgkq" />
              <ResearchSource sourceId="RI-03" status="pending" title="Commercial, traction, and financial evidence" summary="This deck intentionally contains no approved commercial figures, campaign-performance results, customer traction, revenue, financial forecasts, valuation, or funding information." methodology="Attach only reviewed internal records or approved external evidence with owner, date, definition, and calculation notes before adding a business claim to this pitch." data-id="element-1029" data-mp="11l5zbpht51u9" />
              <ResearchSource sourceId="RI-04" status="illustrative" title="Product flow and revenue-stream framing" summary="The campaign flow, reporting direction, and revenue-stream descriptions on this page communicate product and operating direction. They are not evidence of shipped capability, customer adoption, or financial performance." methodology="Illustrative narrative content for investor conversation. Validate against product roadmap, commercial terms, and approved operating evidence before external use." data-id="element-1030" data-mp="my2yb8ua0b26" />
            </div>
          </div>
        </section>

        <section id="contact" className="bg-[#f7f5ef] px-5 py-24 md:px-10 md:py-32" aria-labelledby="contact-heading" data-id="element-1031" data-mp="21unq38d0zgwu">
          <div className="mx-auto max-w-[1280px]" data-id="element-1032" data-mp="2yjdap6g1enmi">
            <MoveDownIcon className="text-[#ff4d17]" size={32} aria-hidden="true" data-id="element-1033" data-mp="2yi230vjh0vq4" />
            <PitchHeading number="09" eyebrow="CONTACT" title="Let’s talk about the product experience category." headingId="contact-heading" className="mt-10" data-id="element-1034" data-mp="2ugoyqpw110lb" />
            <a href="mailto:hello@sampler.example?subject=Sampler%20investor%20conversation" className="mt-10 inline-flex items-center gap-2 rounded-full bg-[#11110f] px-6 py-4 text-sm font-extrabold text-white hover:bg-black/80" data-id="element-1035" data-mp="2kjkckq26l54i">
              START A CONVERSATION{' '}
              <ArrowUpRightIcon size={17} aria-hidden="true" data-id="element-1036" data-mp="z1rwdvs5hnja" />
            </a>
            <p className="mt-8 text-xs font-bold text-black/60" data-id="element-1037" data-mp="yzp2v1rshc9t">
              This investor overview is a conceptual communication surface. It
              contains no verified market size, revenue, traction, fundraising,
              customer, or financial claim.
            </p>
          </div>
        </section>
      </main>
    </div>;
}
type PitchHeadingProps = {
  number: string;
  eyebrow: string;
  title: React.ReactNode;
  headingId?: string;
  light?: boolean;
  className?: string;
};
function PitchHeading({
  number,
  eyebrow,
  title,
  headingId,
  light = false,
  className = ''
}: PitchHeadingProps) {
  const reduceMotion = useReducedMotion();
  return <motion.div initial={reduceMotion ? false : {
    opacity: 0,
    y: 18
  }} whileInView={{
    opacity: 1,
    y: 0
  }} viewport={{
    once: true,
    amount: 0.3
  }} transition={revealTransition} className={className} data-id="element-1038" data-mp="1wos4zyw13ra3">
      <p className={`text-xs font-extrabold tracking-[0.16em] ${light ? 'text-[#72c7e8]' : 'text-[#ff4d17]'}`} data-id="element-1039" data-mp="2d3ss4h6m2te">
        {number} / {eyebrow}
      </p>
      <h2 id={headingId} className="mt-5 max-w-5xl font-display text-5xl font-bold leading-[0.87] md:text-8xl" data-id="element-1040" data-mp="1z8x3mx1ypk6p">
        {title}
      </h2>
    </motion.div>;
}
type RevealCardProps = {
  children: React.ReactNode;
  index: number;
  className: string;
};
function RevealCard({
  children,
  index,
  className
}: RevealCardProps) {
  const reduceMotion = useReducedMotion();
  return <motion.article initial={reduceMotion ? false : {
    opacity: 0,
    y: 16
  }} whileInView={{
    opacity: 1,
    y: 0
  }} viewport={{
    once: true,
    amount: 0.25
  }} transition={{
    ...revealTransition,
    delay: reduceMotion ? 0 : index * 0.06
  }} className={className} data-id="element-1041" data-mp="1gbduzujf23kt">
      {children}
    </motion.article>;
}
function PositionCard({
  title,
  copy
}: {
  title: string;
  copy: string;
}) {
  const reduceMotion = useReducedMotion();
  return <motion.article initial={reduceMotion ? false : {
    opacity: 0,
    y: 16
  }} whileInView={{
    opacity: 1,
    y: 0
  }} viewport={{
    once: true,
    amount: 0.3
  }} transition={revealTransition} className="border border-[#72c7e8]/35 p-6" data-id="element-1042" data-mp="31qji6nt5koa7">
      <CheckIcon className="text-[#72c7e8]" aria-hidden="true" data-id="element-1043" data-mp="2zalfryakp0e9" />
      <h3 className="mt-10 text-xs font-extrabold tracking-[0.14em] text-[#72c7e8]" data-id="element-1044" data-mp="348d6iozheggv">
        {title}
      </h3>
      <p className="mt-4 font-display text-3xl font-bold leading-[0.95]" data-id="element-1045" data-mp="3rxo1tqy2binm">
        {copy}
      </p>
    </motion.article>;
}